import Editor from '@draft-js-plugins/editor';
import { convertFromHTML, convertToHTML } from 'draft-convert';
import * as Draft from 'draft-js';
import {
  ContentState,
  EditorState,
  RichUtils,
} from 'draft-js';
import { decode } from 'html-entities';
import * as Immutable from 'immutable';
import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';

import addLinkPluginPlugin from "../draftJSCustomPlugins/addLinkPlugin";
import { richButtonsPlugin } from '../draftJSRichButtonsPlugin';

const HIGHLIGHT = 'highlight'
const HIGHLIGHTABLE = 'HIGHLIGHTABLE'
const LINK = 'LINK'
const MUTABLE = 'MUTABLE'

const customRenderMap = Draft.DefaultDraftBlockRenderMap.merge(
  Immutable.Map({
    unstyled: {
      element: 'div',
      aliasedElements: ['p'],
    },
  })
)

interface TextEditorProps {
  text?: string;
  boilerplate?: string;
  handleTextChange: (html: string) => void;
  shouldCheckSpelling?: boolean;
}

export const TextEditor: React.FC<TextEditorProps> = ({
  text: propText = '',
  boilerplate,
  handleTextChange: propHandleTextChange,
  shouldCheckSpelling,
}) => {
  const isUpdatingFromProps = useRef(false);
  const lastSentHtml = useRef<string | null>(null);
  const addLinkPlugin = useRef(addLinkPluginPlugin);
  const richButtonsPluginRef = useRef(richButtonsPlugin());

  const contentState = useCallback((html: string) => {
    return convertFromHTML({
      htmlToStyle: (nodeName, node, currentStyle) => {
        if (nodeName === 'mark') {
          return currentStyle.add(HIGHLIGHTABLE);
        } else {
          return currentStyle;
        }
      },
      htmlToEntity: (nodeName, node, createEntity) => {
        if (nodeName === 'a') {
          return createEntity(
            LINK,
            MUTABLE,
            {url: node.href}
          )
        }
      },
    })(html || '');
  }, []);

  const [text, setText] = useState<EditorState>(() =>
    EditorState.createWithContent(contentState(propText))
  );

  const html = useCallback(() => {
    return convertToHTML({
      styleToHTML: (style) => {
        if (style === HIGHLIGHTABLE) {
          return <mark />;
        }
      },
      entityToHTML: (entity, originalText) => {
        if (entity.type === LINK) {
          return <a href={entity.data.url}>{decode(originalText)}</a>;
        }
        return originalText;
      }
    })(text.getCurrentContent());
  }, [text]);

  useEffect(() => {
    if (boilerplate !== undefined) {
      setText(EditorState.createWithContent(contentState(boilerplate)));
      setTimeout(() => {
        propHandleTextChange(html());
      }, 0);
    }
  }, [boilerplate]);

  useEffect(() => {
    // Only update if the text prop actually changed AND it's not just our own update coming back
    // This prevents circular updates where our onChange triggers a prop update that resets cursor position
    if (propText !== undefined && !isUpdatingFromProps.current && propText !== lastSentHtml.current) {
      const currentContent = text.getCurrentContent();
      const currentHtml = convertToHTML({
        styleToHTML: (style) => {
          if (style === HIGHLIGHTABLE) {
            return <mark />;
          }
        },
        entityToHTML: (entity, originalText) => {
          if (entity.type === LINK) {
            return <a href={entity.data.url}>{decode(originalText)}</a>;
          }
          return originalText;
        }
      })(currentContent);

      // Only reset editor state if content actually differs from what's already there
      if (propText !== currentHtml) {
        isUpdatingFromProps.current = true;
        lastSentHtml.current = null; // Reset since we're accepting external update
        setText(EditorState.createWithContent(contentState(propText)));
        setTimeout(() => {
          isUpdatingFromProps.current = false;
        }, 0);
      }
    }
  }, [propText, contentState, text]);

  const handleTextChange = (e: EditorState) => {
    if (isUpdatingFromProps.current) return;

    setText(e);
    setTimeout(() => {
      const htmlContent = convertToHTML({
        styleToHTML: (style) => {
          if (style === HIGHLIGHTABLE) {
            return <mark />;
          }
        },
        entityToHTML: (entity, originalText) => {
          if (entity.type === LINK) {
            return <a href={entity.data.url}>{decode(originalText)}</a>;
          }
          return originalText;
        }
      })(e.getCurrentContent());
      const finalHtml = htmlContent.replace(/<p><\/p>/g, '<br/>').replace(/&nbsp;/g, '<br/>');
      lastSentHtml.current = finalHtml; // Track what we sent to prevent echo updates
      propHandleTextChange(finalHtml);
    }, 0);
  };

  const keyBindingFn = (event: React.KeyboardEvent) => {
    if (Draft.KeyBindingUtil.hasCommandModifier(event as any) && (event as any).keyCode === 72) {
      return HIGHLIGHT;
    }
    return Draft.getDefaultKeyBinding(event as any);
  };

  const onKeyCommand = (command: string, editorState: EditorState) => {
    let newState;

    if (command === HIGHLIGHT) {
      newState = Draft.RichUtils.toggleInlineStyle(text, HIGHLIGHTABLE);
    } else {
      newState = Draft.RichUtils.handleKeyCommand(editorState, command);
    }

    if (newState) {
      setText(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const handleAddLink = () => {
    const editorState = text;
    const selection = editorState.getSelection();
    const link = window.prompt('Paste the link -')
    if (!link) {
      handleTextChange(RichUtils.toggleLink(editorState, selection, null));
      return 'handled';
    }
    const content = editorState.getCurrentContent();
    const contentWithEntity = content.createEntity(LINK, MUTABLE, { url: link });
    const newEditorState = EditorState.push(editorState, contentWithEntity, 'create-entity');
    const entityKey = contentWithEntity.getLastCreatedEntityKey();
    handleTextChange(RichUtils.toggleLink(newEditorState, selection, entityKey));
  };

  const {
    ItalicButton, BoldButton, UnderlineButton, createStyleButton,
    BlockquoteButton, ULButton, H3Button,
  } = richButtonsPluginRef.current;

  const HighlightButton = createStyleButton({ style: HIGHLIGHTABLE, label: 'Highlight', });

  const styleMap = {
    HIGHLIGHTABLE: {
      'background-color': '#FFFF00',
    },
  };

  return (
    <div className="text-editor card is-fullwidth">
      <header className="card-header">
        <div className="myToolbar" style={{margin: '1em'}}>
          <H3Button />
          <BoldButton />
          <ItalicButton />
          <UnderlineButton />
          <BlockquoteButton />
          <ULButton />
          <HighlightButton />
          <button className="interactive-wrapper add-link" id="link-url" onClick={handleAddLink} type="button">
            <span>Link</span>
          </button>
        </div>
      </header>
      <div className="card-content">
        <div className="content landing-page-html-editor">
          <Editor
            blockRenderMap={customRenderMap}
            customStyleMap={styleMap}
            editorState={text}
            handleKeyCommand={onKeyCommand}
            keyBindingFn={keyBindingFn}
            onChange={handleTextChange}
            plugins={[richButtonsPluginRef.current, addLinkPlugin.current]}
            spellCheck={true}
          />
        </div>
      </div>
    </div>
  );
};
